// StickyHeader — Bank name, dealer name, pair filter, feed freshness,
// venue connection, CB reference, dealer limit headroom, NOP gauge, clocks.

import React, { useState, useEffect } from 'react';
import { Globe, Wifi, Clock, AlertTriangle } from 'lucide-react';
import { STALE_FEED_THRESHOLD_MS } from '../../lib/lp/constants';

const StickyHeader = ({ user, priceFeed, cbReference, bookContext, dealerLimits, metrics, pairFilter, onPairFilterChange }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Feed freshness: count stale pairs
  const stalePairs = Object.entries(priceFeed || {}).filter(
    ([, data]) => Date.now() - data.timestamp > STALE_FEED_THRESHOLD_MS
  );
  const feedOk = stalePairs.length === 0;

  // NOP gauge
  const nopPct = bookContext?.nopUtilization != null
    ? (bookContext.nopUtilization * 100).toFixed(0)
    : '—';
  const nopColor = (bookContext?.nopUtilization || 0) > 0.9
    ? '#991b1b'
    : (bookContext?.nopUtilization || 0) > 0.7
      ? '#3f3f46'
      : '#52525b';

  // Dealer daily volume headroom
  const dailyUsedPct = dealerLimits?.dailyVolume
    ? ((dealerLimits.dailyVolume.used / dealerLimits.dailyVolume.max) * 100).toFixed(0)
    : '—';

  const formatTime = (d, tz) => {
    try {
      return d.toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
  };

  return (
    <div className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center gap-6 text-xs sticky top-0 z-30">
      {/* Bank & Dealer */}
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-zinc-400" />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          {user?.tenantName || 'Bank'}
        </span>
        <span className="text-zinc-300">|</span>
        <span className="font-mono text-[10px] text-zinc-600">{user?.name || 'Dealer'}</span>
      </div>

      {/* Pair Filter */}
      <div className="flex items-center gap-1">
        <select
          value={pairFilter}
          onChange={e => onPairFilterChange(e.target.value)}
          className="border border-zinc-200 rounded-none px-2 py-1 font-mono text-[10px] focus:outline-none focus:border-zinc-400 bg-white"
        >
          <option value="all">ALL PAIRS</option>
          {Object.keys(priceFeed || {}).sort().map(pair => (
            <option key={pair} value={pair}>{pair}</option>
          ))}
        </select>
      </div>

      {/* Feed Freshness */}
      <div className="flex items-center gap-1">
        <div className={`w-2 h-2 rounded-full ${feedOk ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Feed {feedOk ? 'Live' : `${stalePairs.length} Stale`}
        </span>
      </div>

      {/* Venue Connection */}
      <div className="flex items-center gap-1">
        <Wifi className="w-3 h-3 text-green-500" />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Venue</span>
      </div>

      {/* CB Reference */}
      {cbReference && (
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            CBK {new Date(cbReference.asOf).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </span>
        </div>
      )}

      <div className="flex-1" />

      {/* Dealer Limit Headroom */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Vol</span>
        <div className="w-16 h-2 bg-zinc-100 rounded-none">
          <div
            className="h-2 rounded-none transition-all"
            style={{ width: `${Math.min(Number(dailyUsedPct) || 0, 100)}%`, backgroundColor: '#52525b' }}
          />
        </div>
        <span className="font-mono text-[10px] text-zinc-600">{dailyUsedPct}%</span>
      </div>

      {/* NOP Gauge */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">NOP</span>
        <div className="w-16 h-2 bg-zinc-100 rounded-none">
          <div
            className="h-2 rounded-none transition-all"
            style={{ width: `${Math.min(Number(nopPct) || 0, 100)}%`, backgroundColor: nopColor }}
          />
        </div>
        <span className="font-mono text-[10px] text-zinc-600">{nopPct}%</span>
        {(bookContext?.nopUtilization || 0) > 0.9 && (
          <AlertTriangle className="w-3 h-3 text-red-600" />
        )}
      </div>

      {/* Clocks */}
      <div className="flex items-center gap-3 text-zinc-500">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span className="font-mono text-[10px]">{formatTime(now, 'Africa/Nairobi')}</span>
          <span className="font-mono text-[9px] text-zinc-400">EAT</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px]">{formatTime(now, 'UTC')}</span>
          <span className="font-mono text-[9px] text-zinc-400">UTC</span>
        </div>
      </div>
    </div>
  );
};

export default StickyHeader;
